import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Clock } from "lucide-react";
import SEO from "@/components/SEO";
import CtaBanner from "@/components/CtaBanner";
import { MARKET_PAGES } from "@/data/market-pages";
import { serviceSchema, faqSchema } from "@/data/schema";

/** International market landing page (USA, UAE). */
export default function MarketPage({ market }) {
  const m = MARKET_PAGES[market];
  const area = { "@type": "Country", name: m.country };
  const schema = [
    ...m.services.map((s, i) => ({
      ...serviceSchema({ name: `${s.t} (${m.country})`, description: s.d, path: m.path, areaServed: area }),
      "@id": `https://orinaenterprise.com${m.path}/#service-${i + 1}`,
    })),
    faqSchema(m.faqs),
  ];

  return (
    <>
      <SEO
        path={m.path}
        schema={schema}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }, { name: m.country, path: m.path }]}
      />

      <section className="border-b-2 border-black bg-[#FFD700] py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="tape-dark">{m.tape}</div>
          <h1 className="display mt-6 max-w-4xl text-4xl font-black leading-[1.02] tracking-tight md:text-5xl lg:text-6xl">{m.h1}</h1>
          <p className="mt-5 max-w-3xl text-base text-black/85 md:text-lg">{m.lead}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact/?type=expert" className="btn-brutal">
              Talk to an expert <ArrowUpRight size={16} />
            </Link>
            <Link to="/contact/?type=audit" className="btn-brutal secondary">Get a free account audit</Link>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <div className="eyebrow">Direct answer</div>
          <h2 className="display mt-3 text-2xl font-black md:text-3xl">What Orina does for brands in the {m.country === "United States" ? "USA" : "UAE"}</h2>
          <p className="mt-4 text-base leading-relaxed text-black/85 md:text-lg">{m.answer}</p>
        </div>
      </section>

      <section className="border-y-2 border-black bg-[#FAFAF7] py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <h2 className="display text-3xl font-black md:text-4xl">Services</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {m.services.map((s) => (
              <div key={s.t} className="card-brutal">
                <h3 className="display text-xl font-black">{s.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-black/75 md:text-base">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <h2 className="display text-3xl font-black md:text-4xl">What to know before you start</h2>
          {m.facts.map((f) => (
            <div key={f.h} className="mt-8">
              <h3 className="display text-xl font-black">{f.h}</h3>
              <p className="mt-2 text-base leading-relaxed text-black/85 md:text-lg">{f.p}</p>
            </div>
          ))}
          <div className="mt-10 flex items-start gap-3 border-2 border-black bg-[#FFD700] p-6">
            <Clock className="mt-1 shrink-0" size={20} />
            <p className="text-base font-medium">{m.timezone}</p>
          </div>
          <ul className="mt-10 space-y-3">
            {["One dedicated account manager", "Weekly performance report", "Fixed monthly retainer, no percentage of ad spend"].map((b) => (
              <li key={b} className="flex items-start gap-3 text-base md:text-lg">
                <CheckCircle2 className="mt-1 shrink-0 text-[#25D366]" size={20} /> {b}
              </li>
            ))}
          </ul>
          <p className="mt-8 text-base text-black/85">
            Also see our <Link to="/web-app-development/" className="font-bold underline decoration-[#FFD700] decoration-2 underline-offset-4">website and app development</Link> service and our{" "}
            <Link to="/services/a-plus-listing-graphics/" className="font-bold underline decoration-[#FFD700] decoration-2 underline-offset-4">A+ Content design</Link> service.
          </p>
        </div>
      </section>

      <section className="border-y-2 border-black bg-[#FAFAF7] py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <h2 className="display text-3xl font-black md:text-4xl">FAQ</h2>
          <div className="mt-8 space-y-4">
            {m.faqs.map((f) => (
              <details key={f.q} className="card-brutal">
                <summary className="cursor-pointer text-base font-bold md:text-lg">{f.q}</summary>
                <p className="mt-3 text-sm text-black/80 md:text-base">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner
        tape={m.tape}
        title="Ready to grow on international marketplaces?"
        subtitle="Book a free 30-minute call. We'll review your accounts or launch plan and tell you what to fix first."
      />
    </>
  );
}
